import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import Logo from "./Logo";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const links = [
  { id: "process", key: "process" },
  { id: "services", key: "services" },
  { id: "stories", key: "stories" },
  { id: "quote", key: "quote" },
  { id: "faq", key: "faq" },
];

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="nav-language-switcher" className="flex items-center rounded-full border border-line bg-white p-0.5">
      {["tr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`nav-lang-${l}`}
          onClick={() => setLang(l)}
          className={`font-mono rounded-full px-3 py-1 text-[11px] font-bold tracking-widest uppercase transition-colors duration-300 ${lang === l ? "bg-obsidian text-paper" : "text-obsidian/50 hover:text-obsidian"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Nav() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${scrolled ? "border-line bg-white/80 backdrop-blur-md" : "border-transparent bg-transparent"}`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1320px] items-center justify-between px-5 sm:px-8">
        <button data-testid="nav-logo-button" onClick={() => go("top")} className="shrink-0">
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative text-sm font-semibold text-obsidian/70 transition-colors duration-300 hover:text-obsidian"
            >
              {t.nav[l.key]}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LangToggle />
          <button
            data-testid="nav-cta-button"
            onClick={() => go("quote")}
            className="group flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-obsidian transition-colors duration-300 hover:bg-obsidian hover:text-paper"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-b border-line bg-white/95 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded px-2 py-3 text-left font-display text-2xl font-black uppercase tracking-tight text-obsidian"
                >
                  {t.nav[l.key]}
                </button>
              ))}
              <div className="mt-3 flex items-center justify-between px-2 pb-2">
                <LangToggle />
                <button
                  data-testid="nav-mobile-cta"
                  onClick={() => go("quote")}
                  className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-obsidian"
                >
                  {t.nav.cta}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
